import type { DocumentContent } from '@/types/documents'
import type { ReaderBlock, ReaderContent, ReaderToken } from '@/types/reader'

export function isReaderContent(value: unknown): value is ReaderContent {
  if (!value || typeof value !== 'object') return false
  const record = value as Partial<ReaderContent>
  return (
    typeof record.id === 'string' &&
    typeof record.sourceType === 'string' &&
    typeof record.title === 'string' &&
    Array.isArray(record.blocks) &&
    Boolean(record.glossary) &&
    typeof record.processorVersion === 'string'
  )
}

export function getBlockText(
  block: Pick<ReaderBlock, 'originalText' | 'tokens'>
) {
  if (block.originalText) return block.originalText
  return block.tokens.map((token) => token.surface).join('')
}

export function getContentText(content: ReaderContent) {
  return content.blocks.map(getBlockText).join('\n')
}

export function getPlayableBlocks(content: ReaderContent) {
  return content.blocks.filter((block) =>
    ['sentence', 'line'].includes(block.type)
  )
}

export function getReaderStats(content: ReaderContent) {
  const tokenCount = content.blocks.reduce(
    (sum, block) =>
      sum + block.tokens.filter((token) => token.kind === 'word').length,
    0
  )

  return {
    blocks: content.blocks.length,
    words: tokenCount,
    glossary: Object.keys(content.glossary).length
  }
}

export function getLegacyDocumentStats(content: DocumentContent) {
  const paragraphCount = content.paragraphs?.length ?? 0
  const sentenceCount =
    content.paragraphs?.reduce(
      (sum, paragraph) => sum + (paragraph.sentences?.length ?? 0),
      0
    ) ?? 0

  return {
    paragraphs: paragraphCount,
    sentences: sentenceCount
  }
}

export function getTokenText(
  token: ReaderToken,
  script: 'simplified' | 'traditional'
) {
  if (script === 'traditional') {
    return token.traditional || token.surface
  }
  return token.simplified || token.surface
}
